import React from 'react';
import { Building2, Users, Award, Phone, Mail, ExternalLink, QrCode } from 'lucide-react';
import b2bData from '../../data/public/b2b-portfolio.json';
import b2bReview from '../../data/public/b2b-review.json';

type B2BReview = {
  id: string;
  강의주제: string;
  후기: string;
};
// B2B 페이지 컴포넌트 - 기관 대상 강의 섭외 정보
const B2B: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* 히어로 섹션 */}
      <section className="relative py-20 overflow-hidden">
        {/* 배경 이미지 */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('/images/b2b-hero-bg.jpg')`
          }}
        />
        
        {/* 오버레이 */}
        <div className="absolute inset-0 bg-black bg-opacity-40"></div>
        
        {/* 콘텐츠 */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10">
          <div className="flex items-center justify-center mb-6">
            <Building2 className="w-12 h-12 text-white mr-4" />
            <h1 className="text-4xl font-bold text-white">B2B 강의섭외</h1>
          </div>
          <p className="text-xl text-gray-100 max-w-3xl mx-auto mb-8">
            공공기관, 지자체, 기업을 대상으로 한<br />
            ChatGPT 활용 교육 전문 강의를 제공합니다.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="mailto:ohdana0808@daum.net"
              className="bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2 shadow-lg"
            >
              <Mail className="w-5 h-5" />
              <span>이메일 문의</span>
            </a>
            <a 
              href="http://pf.kakao.com/_xbrxjxkxj/chat"
              className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition-colors flex items-center justify-center space-x-2 shadow-lg"
            >
              <QrCode className="w-5 h-5" />
              <span>카카오 채널</span>
            </a>
          </div>
        </div>
      </section>

      {/* 서비스 소개 */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              기관 맞춤형 교육 서비스
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              공공기관의 디지털 전환과 업무 효율성 향상을 위한<br />
              전문적이고 체계적인 ChatGPT 교육을 제공합니다.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-gray-50 rounded-xl">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-8 h-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">공공기관 특화</h3>
              <p className="text-gray-600">
                공공기관의 업무 특성에 맞춘 <br />ChatGPT 활용 교육으로
                서비스 품질 향상과 <br />업무 효율성을 동시에 달성
              </p>
            </div>
            <div className="text-center p-6 bg-gray-50 rounded-xl">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">맞춤형 커리큘럼</h3>
              <p className="text-gray-600">
                기관의 업무 특성과 직원 수준을 고려한<br />
                맞춤형 교육 프로그램 설계 및 운영
              </p>
            </div>
            <div className="text-center p-6 bg-gray-50 rounded-xl">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">검증된 전문성</h3>
              <p className="text-gray-600">
                10년 이상의 교육 경험과 13개 자격증,<br />
                정부포상 수상 경력을 바탕으로 한 신뢰할 수 있는 교육
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 강의 사례 */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              주요 강의 사례
            </h2>
            <p className="text-xl text-gray-600">
              다양한 공공기관에서 진행한 ChatGPT 교육의 성공 사례
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {b2bData.slice(0, 6).map((item: any) => (
              <div key={item.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="p-6">
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {item.강의주제}
                    </h3>
                    <div className="text-sm text-primary-600 font-medium">
                      {item.결과.기관명}
                    </div>
                  </div>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">참석자</span>
                      <span className="font-medium">{item.결과.참석자수}명</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">만족도</span>
                      <span className="font-medium">{item.결과.만족도}/5.0</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">재계약</span>
                      <span className={`font-medium ${item.결과.재계약여부 ? 'text-green-600' : 'text-gray-500'}`}>
                        {item.결과.재계약여부 ? '완료' : '진행중'}
                      </span>
                    </div>
                  </div>

                  {item.블로그링크 && (
                    <a
                      href={item.블로그링크}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium text-sm"
                    >
                      <span>자세히 보기</span>
                      <ExternalLink className="w-4 h-4 ml-1" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 강의 리뷰 */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              강의 리뷰
            </h2>
            <p className="text-xl text-gray-600">
              공공기관에서 진행한 ChatGPT 교육의 성공 사례
            </p>
          </div>
        </div>
      </section>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {(b2bReview as B2BReview[]).slice(0, 6).map((item) => (
          <div key={item.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
            <div className="p-6">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {item.강의주제}
                </h3>
              </div>
              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="font-medium">{item.후기}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* 강의 섭외 프로세스 */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              강의 섭외 프로세스
            </h2>
            <p className="text-xl text-gray-600">
              체계적이고 전문적인 강의 섭외 과정
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                1
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">문의 접수</h3>
              <p className="text-gray-600">전화 또는 이메일로 강의 문의</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                2
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">상담 및 분석</h3>
              <p className="text-gray-600">기관 특성 및 교육 요구사항 분석</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                3
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">맞춤 설계</h3>
              <p className="text-gray-600">기관별 맞춤형 커리큘럼 설계</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                4
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">강의 진행</h3>
              <p className="text-gray-600">전문적이고 체계적인 교육 실시</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA 섹션 */}
      <section className="py-16 bg-gradient-to-r from-primary-600 to-primary-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            지금 바로 강의를 문의하세요
          </h2>
          <p className="text-xl text-primary-100 mb-8 max-w-3xl mx-auto">
            공공기관의 디지털 전환과 업무 혁신을 위한<br />
            ChatGPT 전문 교육을 제공합니다.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:010-9321-0093"
              className="bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2"
            >
              <Phone className="w-5 h-5" />
              <span>010-9321-0093</span>
            </a>
            <a 
              href="mailto:ohdana0808@daum.net"
              className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition-colors flex items-center justify-center space-x-2"
            >
              <Mail className="w-5 h-5" />
              <span>ohdana0808@daum.net</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default B2B;

